import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../../../context/useThemeContext";
import ToggleIconButton from "../../../components/buttons/toggleIconButton";
import { hexToRGBA } from "../../../utility/hexToRGB";
import { IoIosAdd } from "react-icons/io";
import styled from "styled-components";
import { useHorizontalScroll } from "../../../hooks/useHorizontalScroll";
import { FaUser } from "react-icons/fa";
import useMainStore from "../../../stores/mainStore";
import { Portal } from "../../../components/portal";
import { NoteTab } from "../tabs/NoteTab";
import useDataStore from "../../../stores/dataStore";
import useTextFilesStore from "../../../stores/textFilesStore";
import useAbsoluteWindowStore from "../../../stores/absoluteWindowStore";
import LeftScrollboxContextMenu from "../../contextMenu/contextMenus/leftScrollboxContextMenu";

function TabsContainer() {
    // Context
    const { theme } = useTheme();

    // Ref
    const scrollRef = useHorizontalScroll();

    // State
    const [hasOverflow, setHasOverflow] = useState(false);

    // Store
    const selectedNoteID = useMainStore((state) => state.data.selectedNoteID);
    const openedNoteIDs = useMainStore((state) => state.data.openedNoteIDs);

    //Effect
    useEffect(() => {
        const updateHasOverflow = () => {
            const isAtEnd =
                scrollRef.current?.scrollLeft +
                    scrollRef.current?.clientWidth ===
                scrollRef.current?.scrollWidth;

            const canScroll =
                scrollRef.current?.scrollWidth > scrollRef.current?.clientWidth;

            if (canScroll && !isAtEnd) {
                setHasOverflow(true);
                return;
            }

            setHasOverflow(false);
        };

        const handleScroll = () => updateHasOverflow();
        const handleResize = () => updateHasOverflow();

        updateHasOverflow();

        scrollRef.current?.addEventListener("scroll", handleScroll);
        window.addEventListener("resize", handleResize);

        return () => {
            scrollRef.current?.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleResize);
        };
    }, [openedNoteIDs]);

    function AddTab() {
        let selectedFolder = useMainStore.getState().data.selectedFolderID;

        // Add new note
        const newNoteID = useDataStore.getState().add.note({
            folderID:
                selectedFolder === "uncategorized" ? undefined : selectedFolder,
        });

        // Open new note
        useMainStore
            .getState()
            .set.openedNoteIDs((prev) => prev && [...prev, newNoteID]);

        // Select new note
        useMainStore.getState().set.selectedNoteID(() => newNoteID);
    }

    function rightClick(e: React.MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
        useAbsoluteWindowStore.getState().setWindowShowing({
            type: "ContextMenu",
            children: <LeftScrollboxContextMenu />,
        });

        useAbsoluteWindowStore.getState().setPosition(e.clientX, e.clientY);
    }

    return (
        <div
            className="flex flex-1 relative h-[inherit] overflow-hidden"
            onContextMenu={rightClick}
        >
            <div
                ref={scrollRef}
                id="tabContainer"
                className="flex flex-shrink-1 overflow-x-auto overflow-y-hidden h-[inherit]  whitespace-nowrap relative gap-[2px] pr-[2px] pl-[2px]"
                style={{
                    scrollbarWidth: "none",
                }}
            >
                {openedNoteIDs?.map((id, index) => {
                    // If not not included for user, return
                    if (!useDataStore.getState().data.notes[id]) return null;

                    let nextIsSelected = false;
                    if (openedNoteIDs[index + 1] === selectedNoteID) {
                        nextIsSelected = true;
                    }
                    // if (i === numberOfTabs - 1) {
                    //     //? Fix this in final implementation
                    //     // no border on last element
                    //     nextIsSelected = true;
                    // }

                    return (
                        <NoteTab
                            key={id}
                            noteID={id}
                            isSelected={id === selectedNoteID}
                            overrideHideSeparator={nextIsSelected}
                            setSelectedTab={() => {
                                useMainStore
                                    .getState()
                                    .set.selectedNoteID(() => id);
                            }}
                        />
                    );
                })}
            </div>
            {/* Add Tab Button */}
            <div className="h-[80%] flex items-center mt-[auto] ml-1">
                <ToggleIconButton
                    alt="Add Tab"
                    onClick={() => {
                        AddTab();
                    }}
                    size={{ x: 28, y: 28 }}
                    iconSize={26}
                    inactiveIconColor={theme.fontColor[100]}
                    hoverIconInactive={theme.fontColor[50]}
                    hoverBGInactive={theme.secondaryHeaderColor}
                    icon={<IoIosAdd />}
                />
            </div>
            {/* Right shadow to indicate overflow */}
            <div
                className="absolute right-[-5px] h-full w-[5px] bg-black blur-[10px] z-0 opacity-50"
                style={{
                    opacity: hasOverflow ? "1" : "0",
                    transition: "all 0.08s ease-in-out",
                }}
            />
        </div>
    );
}

export default TabsContainer;
